// @migration-in-progress
/**
 * VoyageManagementTab Component - Manages voyage lifecycle
 */

import React, { useState, useMemo, useEffect } from 'react';
import { Play } from 'lucide-react';
import { StartVoyageModal, EndVoyageModal } from './VoyageModals';
import VoyageStatistics from './VoyageStatistics';
import VoyageHistory from './VoyageHistory';
import VoyagePhasedInterface from './VoyagePhasedInterface';
// FT-269: preserved for rollback safety per locked decision §7 — classic
// interface file remains in repo even though the toggle button is gone.
// eslint-disable-next-line no-unused-vars
import VoyageClassicInterface from './VoyageClassicInterface';
import VoyageLiveFeed from './VoyageLiveFeed';
import { useVessel } from 'contexts/VesselContext';
import { useCrew } from '../../../../hooks/useCrew';
// FT-269
import ArchivedAbortedVoyagesTab from '../operations/ArchivedAbortedVoyagesTab';
import DraftVoyageBanner from '../operations/ActiveVoyage/DraftVoyageBanner';
import { useVoyages } from '../../../../hooks/domain/operations/useVoyages';

const VoyageManagementTab = ({ 
  voyages = [],
  activeVoyage,
  passagePlans = [],
  currentVessel,
  logEntries = [],
  onStartVoyage,
  onEndVoyage,
  onAddLogEntry,
  onOpenFullLog
}) => {
  // Voyage states: PLANNED -> ACTIVE (Let Go) -> COMPLETED (All Fast)
  const VOYAGE_STATUS = {
    PLANNED: 'planned',
    ACTIVE: 'active', 
    COMPLETED: 'completed',
    CANCELLED: 'cancelled'
  };
  const [showStartVoyage, setShowStartVoyage] = useState(false);
  const [showEndVoyage, setShowEndVoyage] = useState(false);
  const [selectedVoyage, setSelectedVoyage] = useState(null);
  const [preDepartureComplete, setPreDepartureComplete] = useState(false);
  // Use the NEW unified crew system
  const { crew: crewMembers = [] } = useCrew();
  const [voyageForm, setVoyageForm] = useState({
    departurePort: '',
    destinationPort: '',
    purpose: 'commercial',
    passagePlanId: '',
    notes: ''
  });
  const [endForm, setEndForm] = useState({
    arrivalPort: '',
    actualArrivalTime: '',
    fuelUsed: '',
    engineHours: '',
    notes: ''
  });

  // Calculate voyage statistics
  const voyageStats = useMemo(() => {
    const completed = voyages.filter(v => v.endTime);
    const totalDistance = completed.reduce((sum, v) => sum + (v.distance || 0), 0);
    const totalTime = completed.reduce((sum, v) => {
      if (v.startTime && v.endTime) {
        return sum + (new Date(v.endTime) - new Date(v.startTime));
      }
      return sum;
    }, 0);

    return {
      total: voyages.length,
      completed: completed.length,
      active: activeVoyage ? 1 : 0,
      totalDistance,
      totalTime: totalTime / 3600000,
      averageSpeed: totalTime > 0 ? (totalDistance / (totalTime / 3600000)) : 0
    };
  }, [voyages, activeVoyage]);

  // Sort voyages by date
  const sortedVoyages = useMemo(() => {
    return [...voyages].sort((a, b) => {
      const dateA = new Date(a.startTime || 0);
      const dateB = new Date(b.startTime || 0);
      return dateB - dateA;
    });
  }, [voyages]);

  const formatDuration = (hours) => {
    if (hours < 1) return `${Math.round(hours * 60)} mins`;
    if (hours < 24) return `${hours.toFixed(1)} hrs`;
    const days = Math.floor(hours / 24);
    const remainingHours = Math.round(hours % 24);
    return `${days}d ${remainingHours}h`;
  };

  const handleStartVoyage = async () => {
    if (!voyageForm.departurePort || !voyageForm.destinationPort) {
      alert('Please enter departure and destination ports');
      return;
    }
    // Create voyage in PLANNED status - actual start is when "Let Go" is recorded
    const voyageData = {
      ...voyageForm,
      status: VOYAGE_STATUS.PLANNED,
      plannedTime: new Date().toISOString(),
      startTime: null, // Will be set when "Let Go" is recorded
      endTime: null
    };
    const success = await onStartVoyage(voyageData);
    if (success) {
      setShowStartVoyage(false);
      setVoyageForm({
        departurePort: '',
        destinationPort: '',
        purpose: 'commercial',
        passagePlanId: '',
        notes: ''
      });
    }
  };

  // Handle Let Go (actual voyage start)
  const handleLetGo = () => {
    if (!preDepartureComplete) {
      alert('Please complete pre-departure checks before letting go');
      return;
    }
    
    if (activeVoyage && (!activeVoyage.status || activeVoyage.status === VOYAGE_STATUS.PLANNED)) {
      const letGoEntry = {
        type: 'LET_GO',
        category: 'navigation',
        summary: 'Let Go - All Lines',
        description: `Departed ${activeVoyage.departurePort}`,
        voyageId: activeVoyage.id,
        timestamp: new Date().toISOString()
      };
      onAddLogEntry(letGoEntry);
      // Update voyage status to ACTIVE and set actual start time
      const updatedVoyage = {
        ...activeVoyage,
        status: VOYAGE_STATUS.ACTIVE,
        startTime: new Date().toISOString(),
        preDepartureComplete: true
      };
      onStartVoyage(updatedVoyage, true); // true = update existing
    }
  };
  
  // Handle pre-departure checklist completion
  const handlePreDepartureComplete = (checklistData, summary) => {
    setPreDepartureComplete(true);
    // Log the pre-departure checks
    const checkEntry = {
      type: 'PRE_DEPARTURE',
      category: 'safety',
      summary: 'Pre-Departure Checks Complete',
      description: summary.weather,
      details: checklistData,
      voyageId: activeVoyage.id,
      timestamp: new Date().toISOString()
    };
    onAddLogEntry(checkEntry);
  };

  // Handle All Fast (voyage end)
  const handleAllFast = () => {
    if (activeVoyage && activeVoyage.status === VOYAGE_STATUS.ACTIVE) {
      const allFastEntry = {
        type: 'ALL_FAST',
        category: 'navigation',
        summary: 'All Fast - Made Fast',
        description: `Arrived ${activeVoyage.destinationPort || endForm.arrivalPort}`,
        voyageId: activeVoyage.id,
        timestamp: new Date().toISOString()
      };
      onAddLogEntry(allFastEntry);
      setShowEndVoyage(true); // Show modal for optional details
    }
  };

  const handleEndVoyage = async () => {
    if (!endForm.arrivalPort) {
      alert('Please enter arrival port');
      return;
    }
    const success = await onEndVoyage(activeVoyage.id, endForm);
    if (success) {
      setShowEndVoyage(false);
      setEndForm({
        arrivalPort: '',
        actualArrivalTime: '',
        fuelUsed: '',
        engineHours: '',
        notes: ''
      });
    }
  };

  // Handle voyage update (for phased interface)
  const handleUpdateVoyage = (updatedVoyage) => {
    // This should be passed from parent or implemented here
    // For now, using onStartVoyage with update flag
    onStartVoyage(updatedVoyage, true);
  };

  // FT-269: archived/aborted tab + draft banner + server-stamp reconciler.
  const {
    draftVoyage,
    abortDraftVoyage,
    reconcileMissingServerStamps
  } = useVoyages();
  const [activeSubTab, setActiveSubTab] = useState('active'); // 'active' | 'archived'

  // FT-269 §24.2 fallback: reconcile any voyage events that are missing
  // recordedAtServer. Runs on mount and whenever the tab becomes visible.
  useEffect(() => {
    if (typeof reconcileMissingServerStamps !== 'function') return;
    reconcileMissingServerStamps();
    const onVisibility = () => {
      if (typeof document === 'undefined') return;
      if (document.visibilityState === 'visible') {
        reconcileMissingServerStamps();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [reconcileMissingServerStamps]);

  const handleResumeDraft = () => {
    // Surfacing the draft is the Phased Interface's job — switch back to it
    // so the StartVoyageFullScreen mount logic finds the draft and pre-fills.
    setActiveSubTab('active');
  };

  const handleDiscardDraft = (voyageId) => {
    abortDraftVoyage(voyageId, 'user_discarded').catch(err => {
      console.warn('[VoyageManagementTab] abortDraftVoyage failed (non-fatal):', err);
    });
  };

  const handleCancelArchivedPermanently = (voyageId) => {
    abortDraftVoyage(voyageId, 'user_cancelled').catch(err => {
      console.warn('[VoyageManagementTab] abortDraftVoyage failed (non-fatal):', err);
    });
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
      {/* Main Content - 2/3 width */}
      <div className="lg:col-span-2">
        {/* FT-269: DRAFT banner — visible only when an unfinished draft exists. */}
        {draftVoyage && activeSubTab === 'active' && (
          <div className="mb-4">
            <DraftVoyageBanner
              draftVoyage={draftVoyage}
              onResume={handleResumeDraft}
              onDiscard={handleDiscardDraft}
            />
          </div>
        )}

        {/* FT-269: tab strip — Active / Archived & Aborted.
            The classic toggle button has been removed; classic interface
            file is preserved per locked decision §7 for rollback safety. */}
        <div className="mb-4 border-b border-slate-200 flex gap-2" role="tablist" aria-label="Voyage views">
          <button
            type="button"
            role="tab"
            aria-selected={activeSubTab === 'active'}
            onClick={() => setActiveSubTab('active')}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
              activeSubTab === 'active'
                ? 'border-cyan-600 text-cyan-700'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            Active
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeSubTab === 'archived'}
            onClick={() => setActiveSubTab('archived')}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
              activeSubTab === 'archived'
                ? 'border-cyan-600 text-cyan-700'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            Archived &amp; Aborted
          </button>
        </div>

        {activeSubTab === 'active' && (
          <VoyagePhasedInterface
            activeVoyage={activeVoyage}
            onStartVoyage={onStartVoyage}
            onUpdateVoyage={handleUpdateVoyage}
            onEndVoyage={onEndVoyage}
            onAddLogEntry={onAddLogEntry}
            logEntries={logEntries}
            crew={crewMembers}
            passagePlans={passagePlans}
            currentVessel={currentVessel}
            useExpandable={true}
          />
        )}

        {activeSubTab === 'archived' && (
          <ArchivedAbortedVoyagesTab
            voyages={voyages}
            onResume={handleResumeDraft}
            onCancelPermanently={handleCancelArchivedPermanently}
          />
        )}
      </div>

      {/* Sidebar - 1/3 width - Live Feed */}
      <div className="lg:col-span-1">
        <VoyageLiveFeed
          logEntries={logEntries}
          activeVoyage={activeVoyage}
          onOpenFullLog={onOpenFullLog}
        />
      </div>

      {/* Modals */}
      <StartVoyageModal
        isOpen={showStartVoyage}
        onClose={() => setShowStartVoyage(false)}
        voyageForm={voyageForm}
        setVoyageForm={setVoyageForm}
        passagePlans={passagePlans}
        onSave={handleStartVoyage}
      />
      
      <EndVoyageModal
        isOpen={showEndVoyage}
        onClose={() => setShowEndVoyage(false)}
        endForm={endForm}
        setEndForm={setEndForm}
        activeVoyage={activeVoyage}
        onSave={handleEndVoyage}
      />
    </div>
  );
};

export default VoyageManagementTab;